/**
 * Выбор роли связи «объект — организация» (KLS ORGANIZATION_ROLES).
 * Данные с GET /organizations/relationship-roles; поиск по названию и коду.
 */

import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, ChevronsUpDown, HelpCircle, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { organizationsApi } from "@/services/api-v2.service";
import type { OrganizationRelationshipRole } from "@/types/api-v2";

function primaryLabel(r: OrganizationRelationshipRole): string {
  const s = r.name_short?.trim();
  return s || r.name || r.code || "—";
}

function subtitle(r: OrganizationRelationshipRole): string {
  return `${r.code} · id ${r.id}`;
}

export interface OrganizationRoleKlsComboboxProps {
  value: number | "";
  onValueChange: (klsId: number | "") => void;
  disabled?: boolean;
  className?: string;
  /** Подпись для опции «без роли» */
  emptyLabel?: string;
}

export function OrganizationRoleKlsCombobox({
  value,
  onValueChange,
  disabled = false,
  className,
  emptyLabel = "Без роли — только организация в связи",
}: OrganizationRoleKlsComboboxProps) {
  const [open, setOpen] = useState(false);

  const { data: roles = [], isLoading } = useQuery({
    queryKey: ["organizations", "relationship-roles"],
    queryFn: () => organizationsApi.listRelationshipRoles(),
    staleTime: 120_000,
  });

  const selected = useMemo(
    () => (value === "" ? undefined : roles.find((r) => r.id === value)),
    [roles, value],
  );

  const valueStr = value === "" ? "" : String(value);
  const triggerLabel = useMemo(() => {
    if (value === "" || (typeof value === "number" && value <= 0)) {
      return emptyLabel;
    }
    if (selected) {
      return `${primaryLabel(selected)} (${subtitle(selected)})`;
    }
    return `Роль id ${value}`;
  }, [value, selected, emptyLabel]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className={cn("w-full justify-between font-normal", className)}
          >
            <span className="truncate text-left">{triggerLabel}</span>
            <span className="flex shrink-0 items-center gap-1">
              <Tooltip>
                <TooltipTrigger asChild>
                  <HelpCircle
                    className="h-4 w-4 text-muted-foreground"
                    onClick={(e) => e.stopPropagation()}
                    onPointerDown={(e) => e.stopPropagation()}
                  />
                </TooltipTrigger>
                <TooltipContent
                  side="top"
                  className="max-w-xs bg-popover text-popover-foreground border"
                >
                  Роль задаёт смысл связи: кем является выбранная организация по
                  отношению к объекту (собственник, УК, арендатор и т.д.).
                  Справочник отделён от списка организаций.
                </TooltipContent>
              </Tooltip>
              <ChevronsUpDown className="h-4 w-4 opacity-50" />
            </span>
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)] min-w-[300px] p-0 z-[9999]"
          align="start"
        >
          <Command shouldFilter>
            <CommandInput placeholder="Поиск по названию или коду…" />
            <CommandList className="max-h-[280px]">
              {isLoading ? (
                <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Загрузка ролей…
                </div>
              ) : (
                <>
                  <CommandEmpty>Ничего не найдено</CommandEmpty>
                  <CommandGroup heading="Роль в связи с объектом">
                    <CommandItem
                      value="__none__"
                      onSelect={() => {
                        onValueChange("");
                        setOpen(false);
                      }}
                      className="cursor-pointer"
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4 shrink-0",
                          valueStr === "" ? "opacity-100" : "opacity-0",
                        )}
                      />
                      <div className="min-w-0">
                        <div className="font-medium">{emptyLabel}</div>
                        <div className="text-xs text-muted-foreground">
                          Укажите только организацию слева
                        </div>
                      </div>
                    </CommandItem>
                    {roles.map((r) => {
                      const idStr = String(r.id);
                      return (
                        <CommandItem
                          key={r.id}
                          value={`${r.code} ${r.name} ${idStr}`}
                          onSelect={() => {
                            onValueChange(Number(idStr));
                            setOpen(false);
                          }}
                          title={`${primaryLabel(r)} — ${subtitle(r)}`}
                          className="cursor-pointer"
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4 shrink-0",
                              valueStr === idStr ? "opacity-100" : "opacity-0",
                            )}
                          />
                          <div className="min-w-0 flex-1">
                            <div className="truncate font-medium">
                              {primaryLabel(r)}
                            </div>
                            <div className="truncate text-xs text-muted-foreground">
                              {subtitle(r)}
                            </div>
                            {r.description ? (
                              <div className="truncate text-[11px] text-muted-foreground/90 mt-0.5">
                                {r.description}
                              </div>
                            ) : null}
                          </div>
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
  );
}
